import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import * as Dialog from "@radix-ui/react-dialog";
import * as RadioGroup from "@radix-ui/react-radio-group";
import { Cross2Icon } from "@radix-ui/react-icons";
import useRecommendedPortfolio from "../../hooks/useRecommendedPortfolio";
import Loading from "./Loading";
import PortfolioDialog from "./PortfolioDialog";
import { useAnswer } from "../../util/form";
import { useAppStore } from "../../store/store";
import usePortfolioIcon from "../../hooks/usePortfolioIcon";

const ANSWERS = {
  proceed: "I am happy with this portfolio.",
  choose: "I would like to select my own portfolio.",
};

export default function Rtq({
  advanceScreen,
  previousScreen,
  validation,
  updateValidation,
  value,
  screen,
}) {
  const { formOptions, user, initialState } = useAppStore();
  const setSelectedPortfolioId = useAppStore(
    (state) => state.setSelectedPortfolioId,
  );
  const { answer, setAnswer } = useAnswer(screen.id);
  const [portfolio, setPortfolio] = useState("");
  const [tempPortfolio, setTempPortfolio] = useState("");
  const [popoverContent, setPopoverContent] = useState("");

  const { recommendedPortfolio, recommendedPortfolioInfo, loading } =
    useRecommendedPortfolio({
      isRtq: true,
    });
  const recommendedPortfolioName = useMemo(
    () => recommendedPortfolioInfo?.name,
    [recommendedPortfolioInfo],
  );

  const previousAnswer = useMemo(() => {
    const question = initialState?.form?.questions?.find?.(
      (question) => question.questionId === screen.id,
    );
    return ANSWERS[question?.answerKey] ?? "";
  }, [initialState?.form?.questions, screen.id]);
  const showPreviousAnswer = useMemo(
    () => previousAnswer.length > 0,
    [previousAnswer.length],
  );

  const portfolioIcon = usePortfolioIcon(recommendedPortfolioInfo?.id - 1);

  useEffect(() => {
    // if (value && value.length > 0) updateValidation(true);
    if (value === "proceed" || (value === "choose" && portfolio)) {
      updateValidation(true);
    } else {
      updateValidation(false);
    }
  }, [value, updateValidation, portfolio]);

  // Set initial state when recommended portfolio loads
  useEffect(() => {
    if (!loading && recommendedPortfolioInfo?.id) {
      setAnswer("proceed");
      setSelectedPortfolioId(recommendedPortfolioInfo.id);
      updateValidation(true);
    }
  }, [
    loading,
    recommendedPortfolioInfo,
    setAnswer,
    setSelectedPortfolioId,
    updateValidation,
  ]);

  function updateAnswer(value) {
    if (value === "proceed") {
      setPortfolio("");
      setTempPortfolio("");
      const recommendedId =
        recommendedPortfolio?.recommendedPortfolioId ??
        recommendedPortfolioInfo?.id ??
        user.artesysPortfolioCode;
      setAnswer(String(recommendedId));
      setSelectedPortfolioId(recommendedId);
    } else if (value === "choose") {
      setAnswer("choose");
      setTempPortfolio(portfolio); // Initialize temp with current when opening modal
    } else if (portfolio) {
      setAnswer(portfolio);
      setSelectedPortfolioId(portfolio);
    }
    updateValidation(value.length > 0);
  }

  function savePortfolio() {
    setPortfolio(tempPortfolio);
    setAnswer(tempPortfolio);
    setSelectedPortfolioId(tempPortfolio);
    updateValidation(true);
  }

  function cancelPortfolio() {
    setTempPortfolio(portfolio);
  }

  const testSet = (portfolioId) => {
    // Use setTimeout to ensure this state update happens after any other updates
    setTimeout(() => {
      setTempPortfolio(portfolioId);
      console.log("tempPortfolio should be updated to:", portfolioId);
    }, 0);
  };

  const isSaveDisabled = useMemo(() => tempPortfolio === "", [tempPortfolio]);

  useEffect(() => {
    if (recommendedPortfolioInfo?.id) {
      const portfolio = formOptions.portfolios.find(
        (p) => p.id === recommendedPortfolioInfo.id,
      );
      setPopoverContent(portfolio.description);
    }
  }, [recommendedPortfolioInfo, formOptions]);

  if (loading || !recommendedPortfolioInfo?.id) return <Loading />;
  else
    return (
      <motion.div
        className="w-full h-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="px-6 py-16 text-center md:py-20 md:px-16">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl mb-9 md:mb-11 md:text-4xl">
              You have been recommended the portfolio:
            </h1>
          </div>
          <div className="mb-4">
            <span className="text-base font-bold text-gray-800 md:text-lg">
              <strong>{recommendedPortfolioName}</strong>
            </span>
            <Popover.Root>
              <Popover.Trigger asChild>
                <button className="relative ml-[.25em]">
                  <FontAwesomeIcon
                    icon="fa-regular fa-circle-info"
                    className="text-[.944em] text-as-green-100"
                  />
                </button>
              </Popover.Trigger>
              <Popover.Content
                sideOffset={5}
                className="w-64 rounded bg-white p-5 shadow-xl will-change-[transform,opacity] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-as-green-50 data-[side=bottom]:animate-slide-up-fade data-[side=left]:animate-slide-right-fade data-[side=right]:animate-slide-left-fade data-[side=top]:animate-slide-down-fade z-50"
              >
                <div className="z-50 text-xs">
                  <p>{popoverContent}</p>
                </div>
                <Popover.Close
                  className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full font-[inherit] text-gray-500 shadow-none hover:bg-gray-100 hover:text-gray-900"
                  aria-label="Close"
                >
                  <Cross2Icon />
                </Popover.Close>
                <Popover.Arrow className="fill-white" />
              </Popover.Content>
            </Popover.Root>
          </div>
          <img
            className="block w-64 h-auto mx-auto mb-3 md:mb-4 md:w-72"
            src={portfolioIcon}
            alt="Portfolio Icon"
          />
          <div className="flex items-center justify-center mb-10 gap-x-5 md:mb-14">
            <div className="flex items-center gap-x-2">
              <div className="w-6 h-3 bg-as-green-50"></div>
              <div className="text-sm font-bold text-black">Fixed Income</div>
            </div>
            <div className="flex items-center gap-x-2">
              <div className="w-6 h-3 bg-as-green-100"></div>
              <div className="text-sm font-bold text-black">Equities</div>
            </div>
          </div>
          <form>
            <div className="max-w-[37.125rem] mx-auto">
              {showPreviousAnswer && (
                <p className="!mb-4">
                  During your previous session, the path you chose was{" "}
                  <b>{previousAnswer}</b>
                </p>
              )}

              <h2 className="mb-6 text-xl font-light md:text-2xl md:mb-8">
                How would you like to proceed?
              </h2>
              <RadioGroup.Root
                className="space-y-3"
                aria-label="How would you like to proceed?"
                onValueChange={setAnswer}
                value={answer}
              >
                <div className="flex items-center">
                  <RadioGroup.Item
                    className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                    value="proceed"
                    id="proceed"
                    onClick={() => updateAnswer("proceed")}
                  >
                    <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                    <label
                      className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                      htmlFor="proceed"
                    >
                      I am happy with this portfolio.
                    </label>
                  </RadioGroup.Item>
                </div>
                {answer === "proceed" && (
                  <div className="px-6 py-6 text-left border border-gray-300 rounded-md md:mt-12 md:px-12 md:py-8">
                    <p className="text-base font-bold text-gray-800 md:text-lg">
                      You&apos;re ready to put Artesys to work.
                    </p>

                    <p className="mt-4 text-gray-700 md:mt-6">
                      Here&apos;s what we do for you:
                    </p>

                    <ol className="mt-3 text-gray-700 list md:mt-5">
                      <li>
                        Allocate your account into a professionally built
                        portfolio on the Voya platform.
                      </li>
                      <li>
                        Monitor your portfolio and adjust investments as market
                        conditions change.
                      </li>
                      <li>
                        Update you on portfolio performance and investment
                        changes as they occur.
                      </li>
                    </ol>
                  </div>
                )}
                <div className="flex items-center">
                  <RadioGroup.Item
                    className="relative w-full rounded-lg border border-as-green-100 bg-transparent pb-4 pl-14 pr-4 pt-4 text-left before:absolute before:left-4 before:top-[1.1875rem] before:block before:h-4 before:w-4 before:rounded-full before:border before:border-as-green-100 before:bg-transparent before:content-[''] data-[state=checked]:bg-as-green-0 data-[state=checked]:before:bg-as-green-100"
                    value="choose"
                    id="choose"
                    onClick={() => updateAnswer("choose")}
                  >
                    <RadioGroup.Indicator className="absolute left-[1.3125rem] top-6 h-1.5 w-1.5 rounded-full after:absolute after:inset-0 after:block after:h-1.5 after:w-1.5 after:rounded-full after:bg-white after:content-['']" />
                    <label
                      className="block text-sm font-bold leading-normal text-gray-800 cursor-pointer"
                      htmlFor="choose"
                    >
                      I would like to select my own portfolio.
                    </label>
                    {answer === "choose" && (
                      <Dialog.Root>
                        {portfolio === "" ? (
                          <Dialog.Trigger asChild>
                            <div
                              className="inline-flex mt-4 cursor-pointer btn btn--small btn--secondary"
                              role="button"
                              tabIndex={0}
                            >
                              Select Portfolio
                              <FontAwesomeIcon
                                icon="fa-solid fa-plus"
                                className="ml-2.5"
                              />
                            </div>
                          </Dialog.Trigger>
                        ) : (
                          <Dialog.Trigger asChild>
                            <div
                              className="btn btn--secondary mt-4 inline-flex !w-full flex-wrap justify-between gap-2 !py-4 !text-sm cursor-pointer"
                              role="button"
                              tabIndex={0}
                            >
                              <span className="text-left text-gray-800">
                                {formOptions.portfoliosKey[
                                  `portfolio${portfolio}`
                                ] || "Select a portfolio"}
                              </span>
                              <span className="font-medium text-right text-teal-800 underline hover:text-as-green-100">
                                Change
                              </span>
                            </div>
                          </Dialog.Trigger>
                        )}

                        <Dialog.Portal>
                          <Dialog.Overlay className="fixed inset-0 z-[100] animate-fade-in overflow-y-auto overflow-x-hidden bg-teal-900/80">
                            <div className="max-w-full wrapper">
                              <Dialog.Content
                                className="w-full max-w-6xl mx-auto my-12 bg-white rounded-md shadow-xl"
                                onInteractOutside={(event) =>
                                  event.preventDefault()
                                }
                              >
                                <div className="p-6">
                                  <Dialog.Title className="text-2xl text-gray-800">
                                    Select Portfolio
                                  </Dialog.Title>
                                  <Dialog.Description className="mt-2">
                                    Choose which portfolio makes sense for you.
                                  </Dialog.Description>
                                </div>
                                <div className="px-6 pb-6">
                                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                                    <PortfolioDialog
                                      portfolio={tempPortfolio}
                                      setPortfolio={testSet}
                                      recommendedPortfolioName={
                                        recommendedPortfolioName
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="sticky bottom-0 p-6 bg-white border-t-2 shadow-sm border-t-gray-200">
                                  <div className="flex gap-3 md:justify-end md:gap-5">
                                    <Dialog.Close asChild>
                                      <button
                                        className="btn btn--secondary btn--small"
                                        onClick={cancelPortfolio}
                                      >
                                        Cancel
                                      </button>
                                    </Dialog.Close>
                                    <Dialog.Close asChild>
                                      <button
                                        className="btn btn--primary btn--small"
                                        disabled={isSaveDisabled}
                                        onClick={savePortfolio}
                                      >
                                        Save
                                      </button>
                                    </Dialog.Close>
                                  </div>
                                </div>
                              </Dialog.Content>
                            </div>
                          </Dialog.Overlay>
                        </Dialog.Portal>
                      </Dialog.Root>
                    )}
                  </RadioGroup.Item>
                </div>
              </RadioGroup.Root>

              <div className="flex justify-center gap-3 mt-6 md:mt-10 md:gap-5">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={previousScreen}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={Boolean(validation) === false}
                  onClick={advanceScreen}
                >
                  Continue
                </button>
              </div>
            </div>
          </form>
        </div>
      </motion.div>
    );
}
